// Mount the Dayoffs package (#204): build its routes from what it needs from
// this app — the generic host (modals, the signed-in user and permissions:
// host-base.ts), the initialised i18n instance (it registers its `dayoffs`
// namespace there), the Excel export, the entity lookups, the date presets,
// the error page and the one page of another module it links to. App.tsx
// mounts the routes from HERE, so the package cannot be mounted unregistered.
// configs/i18n is imported (and so initialised) before the registration runs,
// whatever order main.tsx imports things in. The HTTP client goes through
// core's registry (utils/axios/register-core.ts).
import { ExcelExportButton } from '@/components/ExcelExportButton'
import {
    createDayoffsModule,
    type DayoffsHost,
} from '@vnetwork/datacentral-dayoffs'
import { hostBase } from '@/app/host-base'
import i18n from '@/configs/i18n'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import { paths } from '@/utils/path'
import { DATE_RANGE_PRESETS } from '@/utils/table'

// Lazy: SimpleAsyncSelect/config imports the employee, customer, department,
// position and ticket services. Imported eagerly here it would land in the
// entry chunk (+66 KiB, measured); before #204 it loaded with the Dayoffs
// route chunk. Each call loads it on first use, when a filter opens.
const loadEntityConfig = (entity: 'department' | 'employee') =>
    import('@/components/Form/Shared/SimpleAsyncSelect/config').then((m) =>
        m.getEntityConfig(entity)
    )
const getEntityConfig: DayoffsHost['getEntityConfig'] = (entity) => ({
    fetchOptions: async (query, params) =>
        (await loadEntityConfig(entity)).fetchOptions(query, params),
    fetchOptionsWithPage: async (query, page, params) =>
        (await loadEntityConfig(entity)).fetchOptionsWithPage(
            query,
            page,
            params
        ),
    fetchByIds: async (ids, params) =>
        (await loadEntityConfig(entity)).fetchByIds(ids, params),
})

// The package does not import the app's ErrorPage; the host adds it as each
// route's errorElement (as it does for Warehouse, #59).
export const DayoffsRoutes = createDayoffsModule({
    ...hostBase,
    i18n,
    links: { account: paths.account },
    ErrorPage,
    ExcelExportButton,
    getEntityConfig,
    dateRangePresets: DATE_RANGE_PRESETS,
}).map((route) => ({ ...route, errorElement: <ErrorPage status={500} /> }))
